import React, { useState, useEffect, useCallback } from 'react';
import {
  Brain, RefreshCw, Loader2, CheckCircle, XCircle, AlertTriangle,
  Save, History, Sparkles, Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Provider metadata — labels, colours, descriptions for the UI.
// Order here drives the order of the radio buttons in each row.
const PROVIDERS = [
  {
    id: 'gemini',
    label: 'Google',
    sublabel: 'Gemini 2.5 Flash',
    color: 'bg-sky-100 text-sky-800 border-sky-300',
    activeColor: 'bg-sky-600 text-white border-sky-600',
    description: 'Native Google Gemini SDK. Primary AI provider.',
  },
  {
    id: 'openai',
    label: 'OpenAI',
    sublabel: 'GPT-4o-mini',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    activeColor: 'bg-emerald-600 text-white border-emerald-600',
    description: 'Native OpenAI SDK. Fallback provider.',
  },
];

function fmtAgo(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}

// Live status pill derived from the last 5 call outcomes.
function HealthPill({ recentCalls }) {
  if (!recentCalls?.length) {
    return (
      <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]" data-testid="health-pill-no-data">
        no recent calls
      </Badge>
    );
  }
  const successes = recentCalls.filter(c => c.success).length;
  const rate = (successes / recentCalls.length) * 100;
  if (rate >= 80) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]" data-testid="health-pill-healthy">
        <CheckCircle className="inline h-2.5 w-2.5 mr-0.5" /> healthy · {successes}/{recentCalls.length}
      </Badge>
    );
  }
  if (rate >= 40) {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]" data-testid="health-pill-degraded">
        <AlertTriangle className="inline h-2.5 w-2.5 mr-0.5" /> degraded · {successes}/{recentCalls.length}
      </Badge>
    );
  }
  return (
    <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]" data-testid="health-pill-down">
      <XCircle className="inline h-2.5 w-2.5 mr-0.5" /> failing · {successes}/{recentCalls.length}
    </Badge>
  );
}

export default function AIRoutingSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // pendingOverrides: { [serviceName]: providerId } — only services the
  // user has touched since last save. Empty = nothing to save.
  const [pendingOverrides, setPendingOverrides] = useState({});
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState([]);

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/llm-routing`, { headers });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(`HTTP ${res.status}: ${err.slice(0, 200)}`);
      }
      const json = await res.json();
      setData(json);
      setPendingOverrides({});
    } catch (e) {
      toast.error(`Failed to load AI routing config: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAudit = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/llm-routing/audit?limit=25`, { headers });
      if (res.ok) {
        const json = await res.json();
        setAudit(json.audit || []);
      }
    } catch (e) { /* non-fatal */ }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => { if (showAudit) fetchAudit(); }, [showAudit, fetchAudit]);

  // Effective provider for a service = pending override (if user touched it) else server-stored value.
  const effectiveProviderFor = (svc) => pendingOverrides[svc.name] || svc.current_provider;

  const setProviderFor = (serviceName, providerId) => {
    setPendingOverrides(prev => ({ ...prev, [serviceName]: providerId }));
  };

  const hasUnsaved = Object.keys(pendingOverrides).length > 0;

  const handleSave = async () => {
    if (!hasUnsaved) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/llm-routing`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ overrides: pendingOverrides }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(`HTTP ${res.status}: ${err.slice(0, 200)}`);
      }
      const result = await res.json();
      const changeCount = (result.changes || []).length;
      toast.success(
        changeCount > 0
          ? `Saved ${changeCount} provider change${changeCount > 1 ? 's' : ''}. Takes effect within 60 seconds.`
          : 'No changes to save.',
      );
      await fetchConfig();
      if (showAudit) await fetchAudit();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const providerKeyHealth = data?.providers_health || [];
  const keyHealthFor = (p) => providerKeyHealth.find(h => h.provider === p)?.key_present;

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="ai-routing-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="ai-routing-section">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Brain className="h-4 w-4 text-violet-600" />
            AI Provider Routing
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Pick which AI provider each service uses. Changes are audited and take effect within 60 seconds (no redeploy).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchConfig} disabled={loading}
                  data-testid="ai-routing-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowAudit(v => !v)}
                  data-testid="ai-routing-audit-toggle">
            <History className="h-3.5 w-3.5 mr-1" /> {showAudit ? 'Hide' : 'Show'} Audit
          </Button>
          <Button size="sm"
                  className={hasUnsaved ? 'bg-violet-600 hover:bg-violet-700 text-white' : ''}
                  disabled={!hasUnsaved || saving}
                  onClick={handleSave}
                  data-testid="ai-routing-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save {hasUnsaved ? `(${Object.keys(pendingOverrides).length})` : ''}
          </Button>
        </div>
      </div>

      {/* Provider key health row */}
      <div className="grid grid-cols-2 gap-3" data-testid="provider-key-health">
        {PROVIDERS.map(p => (
          <div key={p.id} className="border rounded-lg px-3 py-2 flex items-center justify-between bg-white">
            <div>
              <div className="text-xs font-semibold text-gray-700">{p.label}</div>
              <div className="text-[10px] text-gray-500">{p.sublabel}</div>
            </div>
            {keyHealthFor(p.id) ? (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]"
                     data-testid={`provider-health-${p.id}-ok`}>
                <Sparkles className="inline h-2.5 w-2.5 mr-0.5" /> key set
              </Badge>
            ) : (
              <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]"
                     data-testid={`provider-health-${p.id}-missing`}>
                <XCircle className="inline h-2.5 w-2.5 mr-0.5" /> key missing
              </Badge>
            )}
          </div>
        ))}
      </div>

      {/* Services table */}
      <div className="border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left w-1/3">Service</th>
              <th className="px-3 py-2 text-center" colSpan={PROVIDERS.length}>Provider</th>
              <th className="px-3 py-2 text-right w-44">Live status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {(data?.services || []).map(svc => {
              const current = effectiveProviderFor(svc);
              const isPending = pendingOverrides[svc.name] && pendingOverrides[svc.name] !== svc.current_provider;
              return (
                <tr key={svc.name} className={isPending ? 'bg-violet-50/40' : ''}
                    data-testid={`ai-routing-row-${svc.name}`}>
                  <td className="px-3 py-3 align-top">
                    <div className="text-sm font-semibold text-gray-800">{svc.label}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">{svc.description}</div>
                    {isPending && (
                      <Badge className="bg-violet-100 text-violet-800 border-violet-200 text-[10px] mt-1"
                             data-testid={`row-pending-${svc.name}`}>
                        unsaved change
                      </Badge>
                    )}
                  </td>
                  {PROVIDERS.map(p => {
                    const selected = current === p.id;
                    const keyMissing = !keyHealthFor(p.id);
                    return (
                      <td key={p.id} className="px-2 py-3 text-center">
                        <button
                          onClick={() => setProviderFor(svc.name, p.id)}
                          disabled={keyMissing}
                          data-testid={`provider-radio-${svc.name}-${p.id}`}
                          title={keyMissing ? `${p.label} key not configured on the server` : `Switch ${svc.label} to ${p.label}`}
                          className={`inline-flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-md border text-[11px] font-medium transition-all
                            ${selected ? p.activeColor : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}
                            ${keyMissing ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                          `}>
                          <span>{p.label}</span>
                          <span className="text-[9px] opacity-80">{p.sublabel}</span>
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-3 py-3 text-right align-top">
                    <HealthPill recentCalls={svc.recent_calls} />
                    {svc.recent_calls?.[0] && (
                      <div className="text-[10px] text-gray-400 mt-1">
                        last: {svc.recent_calls[0].provider} · {fmtAgo(svc.recent_calls[0].created_at)}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footnote about services not yet on uniform routing */}
      <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
        <Zap className="inline h-3 w-3 mr-1 text-gray-400" />
        OBD PDF Parser and Loan Bank Statement use PDF-upload APIs that don't yet share the
        uniform vision pipeline — they're tracked separately and will appear here in v2.
      </div>

      {/* Audit drawer */}
      {showAudit && (
        <div className="border rounded-lg bg-white" data-testid="ai-routing-audit-drawer">
          <div className="px-3 py-2 bg-slate-50 border-b">
            <span className="text-xs font-semibold text-gray-700">Recent Routing Changes</span>
          </div>
          {audit.length === 0 ? (
            <div className="px-3 py-4 text-xs text-gray-500 text-center">No routing changes recorded yet.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-1.5 text-left">Service</th>
                  <th className="px-3 py-1.5 text-left">From</th>
                  <th className="px-3 py-1.5 text-left">To</th>
                  <th className="px-3 py-1.5 text-left">By</th>
                  <th className="px-3 py-1.5 text-right">When</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {audit.map((a, i) => (
                  <tr key={i} data-testid={`audit-row-${i}`}>
                    <td className="px-3 py-1.5 text-gray-700">{a.service_name}</td>
                    <td className="px-3 py-1.5 text-gray-500">{a.from_provider || <span className="italic">default</span>}</td>
                    <td className="px-3 py-1.5 font-medium text-gray-800">{a.to_provider || <span className="italic">default</span>}</td>
                    <td className="px-3 py-1.5 text-gray-500">{a.actor_email || a.actor_id?.slice(0,8) || '—'}</td>
                    <td className="px-3 py-1.5 text-right text-gray-400">{fmtAgo(a.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
